import React, { useEffect, useState } from 'react'
import {Globe, X as XIcon, Facebook, Instagram, Music2, BarChart2, MessageCircleDashed as MessageCircle, ExternalLink, ArrowLeft, Play, Edit, Mail, Send, Image as ImageIcon, Copy, Check, Heart} from 'lucide-react'
import { Project } from '../hooks/useProjects'
import { ProfileEditModal } from './ProfileEditModal'
import { ReviewSection } from './ReviewSection'
import { MediaComments } from './MediaComments'
import { useLikes } from '../hooks/useLikes'
import { useComments } from '../hooks/useComments'

interface User {
  userId: string
  userName: string
  email: string
}

interface ProjectProfilePageProps {
  project: Project
  onBack: () => void
  currentUser?: User | null
  isAdmin?: boolean
}

// Media Item Component with likes and comments
interface MediaItemProps {
  media: any
  index: number
  projectId: string
  currentUserId?: string
  onClick: () => void
}

function MediaItem({ media, index, projectId, currentUserId, onClick }: MediaItemProps) {
  const { likes, isLiked, likeCount, toggleLike } = useLikes(projectId, index, currentUserId)
  const { comments } = useComments(projectId, index)
  const [liking, setLiking] = useState(false)
  
  const handleLikeClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!currentUserId || liking) return
    
    setLiking(true)
    try {
      await toggleLike(currentUserId)
    } catch (error) {
      console.error('Failed to like:', error)
    } finally {
      setLiking(false)
    }
  }
  
  const userHasLiked = currentUserId ? isLiked(currentUserId) : false
  const commentCount = comments.length
  
  return (
    <div 
      className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-[#0052FF] transition-all group cursor-pointer relative"
      onClick={onClick}
    >
      {/* Media Content */}
      <div className="relative">
        {media.type === 'image' ? (
          <img
            src={media.url}
            alt={media.caption || 'Project media'}
            className="w-full h-64 object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <>
            <video
              src={media.url}
              controls
              className="w-full h-64 object-cover"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Play className="w-16 h-16 text-white/80" />
            </div>
          </>
        )}
        
        {/* Heart overlay on image */}
        <button
          onClick={handleLikeClick}
          disabled={liking}
          className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full hover:bg-black/80 transition-all group/like"
        >
          <Heart
            className={`w-5 h-5 transition-all ${
              userHasLiked
                ? 'fill-[#0052FF] text-[#0052FF]'
                : 'text-white group-hover/like:text-[#0052FF] group-hover/like:scale-110'
            }`}
          />
          <span className={`text-sm font-semibold ${userHasLiked ? 'text-[#0052FF]' : 'text-white'}`}>
            {likeCount}
          </span>
        </button>
      </div>
      
      {/* Caption */}
      {media.caption && (
        <div className="p-3">
          <p className="text-gray-300 text-sm">{media.caption}</p>
        </div>
      )}
      
      {/* Comments counter */}
      <div className="p-3 border-t border-white/10 flex items-center justify-center gap-2 hover:bg-white/5 transition-colors">
        <MessageCircle className="w-4 h-4 text-[#0052FF]" />
        <span className="text-[#0052FF] font-semibold text-sm">
          {commentCount} {commentCount === 1 ? 'Comment' : 'Comments'}
        </span>
      </div>
    </div>
  )
}

export function ProjectProfilePage({ project, onBack, currentUser }: ProjectProfilePageProps) {
  const [showEditModal, setShowEditModal] = useState(false)
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null)
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null)
  
  // Check if current user owns this project
  const isOwner = currentUser && project.ownerId === currentUser.userId
  
  // Copy to clipboard function
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(label)
      setTimeout(() => setCopiedAddress(null), 2000)
    })
  }
  
  const socialIcons: Record<string, any> = {
    twitter: XIcon,
    facebook: Facebook,
    instagram: Instagram,
    tiktok: Music2,
    chart: BarChart2,
    farcaster: MessageCircle,
    email: Mail,
    telegram: Send,
    baseApp: Globe,
    nftMarketplace: ImageIcon,
  }
  
  const socialLabels: Record<string, string> = {
    twitter: 'X.com',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    chart: 'Chart',
    farcaster: 'Farcaster',
    baseApp: 'Base App',
    email: 'Email',
    telegram: 'Telegram',
    nftMarketplace: 'NFTs',
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] pb-16">
      {/* Banner */}
      <div className="relative h-80 overflow-hidden">
        {project.bannerImageUrl ? (
          <img
            src={project.bannerImageUrl}
            alt={project.projectName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0052FF] to-[#60A5FA]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0E27]" />
        
        {/* Back Button */}
        <button
          onClick={onBack}
          className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-sm text-white rounded-lg hover:bg-black/70 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
      </div>
      
      {/* Profile Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10 pb-20">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {/* Profile Image */}
          <div className="flex-shrink-0">
            {project.profileImageUrl ? (
              <img
                src={project.profileImageUrl}
                alt={project.projectName}
                className="w-32 h-32 rounded-full object-cover border-4 border-[#0A0E27] shadow-xl"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#0052FF] to-[#60A5FA] flex items-center justify-center border-4 border-[#0A0E27] shadow-xl">
                <span className="text-5xl font-bold text-white">{project.projectName.charAt(0)}</span>
              </div>
            )}
          </div>
          
          {/* Project Info */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <h1 className="text-4xl font-bold text-white">{project.projectName}</h1>
                {project.isDemo && (
                  <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-full text-sm font-bold">
                    DEMO
                  </span>
                )}
                {project.isFeatured && (
                  <span className="px-3 py-1 bg-[#FFD700]/20 border border-[#FFD700]/30 text-[#FFD700] rounded-full text-sm font-bold">
                    Featured
                  </span>
                )}
              </div>
              {isOwner && (
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  <Edit className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
            </div>
            
            <p className="text-gray-300 text-lg mb-4">
              {project.description || 'No description provided'}
            </p>
            
            {/* Blockchain & Contract Info */}
            <div className="flex flex-wrap gap-3 mb-6">
              {project.blockchain && (
                <div className="px-3 py-1 bg-[#0052FF]/20 border border-[#0052FF]/30 text-[#0052FF] rounded-lg text-sm font-semibold">
                  {project.blockchain}
                </div>
              )}
              {project.tokenContractAddress && (
                <button
                  onClick={() => copyToClipboard(project.tokenContractAddress!, 'token')}
                  className="group flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 text-gray-300 rounded-lg text-sm font-mono hover:bg-white/10 hover:border-[#0052FF] transition-all"
                >
                  <span>Token: {project.tokenContractAddress.slice(0, 6)}...{project.tokenContractAddress.slice(-4)}</span>
                  {copiedAddress === 'token' ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 opacity-50 group-hover:opacity-100" />
                  )}
                </button>
              )}
              {project.nftContractAddress && (
                <button
                  onClick={() => copyToClipboard(project.nftContractAddress!, 'nft')}
                  className="group flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 text-gray-300 rounded-lg text-sm font-mono hover:bg-white/10 hover:border-[#0052FF] transition-all"
                >
                  <span>NFT: {project.nftContractAddress.slice(0, 6)}...{project.nftContractAddress.slice(-4)}</span>
                  {copiedAddress === 'nft' ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 opacity-50 group-hover:opacity-100" />
                  )}
                </button>
              )}
            </div>
            
            {/* Social Links */}
            <div className="flex flex-wrap gap-3">
              {project.socialLinks && Object.entries(project.socialLinks)
                .filter(([key, url]) => key !== 'website' && url && url.trim().length > 0) // Exclude website and empty values
                .map(([key, url]) => {
                  const Icon = socialIcons[key]
                  const label = socialLabels[key]
                  
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-300 hover:bg-white/10 hover:border-[#0052FF] hover:text-white transition-all"
                    >
                      {Icon ? <Icon className="w-5 h-5" /> : <Globe className="w-5 h-5" />}
                      <span>{label}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )
                })}
            </div>
          </div>
        </div>
        
        {/* Main CTA - Only show if website URL exists */}
        {(project.websiteUrl || project.socialLinks?.website) && (
          <a
            href={project.websiteUrl || project.socialLinks?.website}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-4 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white text-center rounded-xl font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#0052FF]/50 mb-8"
          >
            Visit Project Website
          </a>
        )}
        
        {/* Live Chart Widget */}
        {project.socialLinks?.chart && (() => {
          // Convert Dexscreener URL to embed format
          let chartUrl = project.socialLinks.chart.trim()
          
          // Ensure protocol exists
          if (!chartUrl.startsWith('http://') && !chartUrl.startsWith('https://')) {
            chartUrl = 'https://' + chartUrl
          }
          
          // Parse and reconstruct the embed URL properly
          let embedUrl = chartUrl
          if (chartUrl.includes('dexscreener.com')) {
            try {
              const url = new URL(chartUrl)
              // Extract the path after dexscreener.com (e.g., /base/0x123... or /base/chainname)
              let path = url.pathname
              
              // Remove leading slash and /embed/ if it exists
              path = path.replace(/^\//, '').replace('embed/', '')
              
              // For Dexscreener embeds: chain/pairaddress format
              // Example: base/0xcda99181... becomes https://dexscreener.com/base/0xcda99181...?embed=1&theme=dark
              embedUrl = `https://dexscreener.com/${path}?embed=1&theme=dark&info=0`
            } catch (e) {
              console.error('Failed to parse Dexscreener URL:', e)
              // Fallback: add query params to original URL
              embedUrl = chartUrl + (chartUrl.includes('?') ? '&' : '?') + 'embed=1&theme=dark&info=0'
            }
          }
          
          return (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <BarChart2 className="w-6 h-6 text-[#0052FF]" />
                Live Chart
              </h2>
              <a
                href={project.socialLinks.chart}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="relative bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-[#0052FF] transition-all">
                  {/* Chart Embed */}
                  <div className="relative w-full h-[500px]">
                    <iframe
                      src={embedUrl}
                      className="w-full h-full"
                      title="Trading Chart"
                      frameBorder="0"
                      allow="clipboard-write; fullscreen"
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    {/* Click overlay hint */}
                    <div className="absolute inset-0 bg-transparent group-hover:bg-[#0052FF]/5 transition-colors pointer-events-none" />
                  </div>
                  {/* Click to expand hint */}
                  <div className="px-4 py-3 bg-white/5 border-t border-white/10 flex items-center justify-center gap-2 group-hover:bg-white/10 transition-colors">
                    <ExternalLink className="w-4 h-4 text-[#0052FF]" />
                    <span className="text-[#0052FF] font-semibold text-sm">Click to open full chart</span>
                  </div>
                </div>
              </a>
            </div>
          )
        })()}
        
        {/* Media Gallery */}
        {project.mediaGallery && project.mediaGallery.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Media Gallery</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.mediaGallery.map((media, index) => (
                <MediaItem
                  key={index}
                  media={media}
                  index={index}
                  projectId={project._id}
                  currentUserId={currentUser?.userId}
                  onClick={() => setSelectedMediaIndex(index)}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Review Section - Always visible to everyone */}
        <ReviewSection 
          projectId={project._id} 
          currentUserId={currentUser?.userId}
        />
      </div>
      
      {/* Edit Profile Modal */}
      {showEditModal && isOwner && (
        <ProfileEditModal
          project={project}
          onClose={() => setShowEditModal(false)}
        />
      )}
      
      {/* Media Comments Modal - Public viewing for everyone */}
      {selectedMediaIndex !== null && project.mediaGallery && (
        <MediaComments
          projectId={project._id}
          mediaIndex={selectedMediaIndex}
          mediaUrl={project.mediaGallery[selectedMediaIndex]?.url}
          mediaType={project.mediaGallery[selectedMediaIndex]?.type}
          mediaCaption={project.mediaGallery[selectedMediaIndex]?.caption}
          currentUserId={currentUser?.userId}
          onClose={() => setSelectedMediaIndex(null)}
        />
      )}
      
      {/* Note: Footer is now rendered in App.tsx for all pages */}
    </div>
  )
}
